import { Queue } from '@rossum/api-client/queues';
import { DetailDrawer } from '@rossum/ui';
import {
  AddRounded,
  ArrowBackRounded,
  ViewColumnRounded,
} from '@rossum/ui/icons';
import {
  Button,
  Grow,
  IconButton,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { SwitchTransition } from 'react-transition-group';
import { isUserAdmin } from '../../../../redux/modules/user/selectors';
import {
  MetaColDef,
  SchemaColDef,
} from '../../../document-list-base/mql/types';
import { AddColumns } from './AddColumns';
import { ColumnList } from './ColumnList';
import { useCustomizeColumns } from './hooks/useCustomizeColumns';

type Props = {
  columns: Array<MetaColDef | SchemaColDef>;
  selectionActive: boolean;
  activeQueue: Queue | null;
};

type Views = 'columnList' | 'addColumns';
const defaultView: Views = 'columnList';

export const ColumnsPanel = ({
  columns,
  selectionActive,
  activeQueue,
}: Props) => {
  const intl = useIntl();
  const [view, setView] = useState<Views>(defaultView);
  const [isOpen, setIsOpen] = useState(false);
  const isUserAdminLike = useSelector(isUserAdmin);

  const { handleAddColumn, handleRemoveColumn } = useCustomizeColumns({
    columns,
    activeQueue,
  });

  const handleClose = () => {
    setIsOpen(false);
    setView(defaultView);
  };

  return isUserAdminLike ? (
    <Stack>
      <IconButton
        color="secondary"
        disabled={selectionActive}
        onClick={() => setIsOpen(true)}
      >
        <ViewColumnRounded />
      </IconButton>

      <DetailDrawer
        open={isOpen}
        onClose={handleClose}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => setView('columnList')}
              sx={{
                visibility: view === 'addColumns' ? 'visible' : 'hidden',
              }}
            >
              <ArrowBackRounded />
            </IconButton>

            <Typography variant="h5">
              {intl.formatMessage({
                id:
                  view === 'columnList'
                    ? 'containers.allDocuments.columnsPanel.title.listView'
                    : 'containers.allDocuments.columnsPanel.title.addView',
              })}
            </Typography>
          </Stack>
        }
        PaperProps={{
          sx: { width: 450 },
        }}
      >
        <Stack px={3} height={1}>
          <SwitchTransition>
            <Grow key={view} appear unmountOnExit timeout={200}>
              <Stack overflow="hidden" flex={1}>
                {view === 'columnList' ? (
                  <Stack pb={4} overflow="hidden">
                    <Button
                      startIcon={<AddRounded />}
                      color="secondary"
                      sx={{ width: 'fit-content', mt: 3, mb: 2, mx: 'auto' }}
                      onClick={() => setView('addColumns')}
                    >
                      {intl.formatMessage({
                        id: 'containers.allDocuments.columnsPanel.button.add',
                      })}
                    </Button>

                    <ColumnList
                      columns={columns}
                      activeQueue={activeQueue}
                      handleRemoveColumn={handleRemoveColumn}
                    />
                  </Stack>
                ) : (
                  <AddColumns
                    activeQueue={activeQueue}
                    handleAddColumn={handleAddColumn}
                    schemaColumns={columns.flatMap(c =>
                      c.columnType === 'schema' ? [c] : []
                    )}
                    handleRemoveColumn={handleRemoveColumn}
                  />
                )}
              </Stack>
            </Grow>
          </SwitchTransition>
        </Stack>
      </DetailDrawer>
    </Stack>
  ) : null;
};
